import React from 'react';
import { IoCalendarOutline, IoRepeat, IoClose } from 'react-icons/io5';
import KitButton from "../kit/Button";
import IconButton from "../kit/IconButton";
import OptionCard from "../kit/OptionCard";
import { FaPlus } from 'react-icons/fa6';
import RepeatingEventSettings from './RepeatingEventSettings';
import './RecurrenceSelector.css';

// Helper to format 24h time to 12h format
const formatTime12h = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes}${ampm}`;
};

// Helper to parse date string (YYYY-MM-DD) as local date to avoid timezone issues
const parseDateAsLocal = (dateString) => {
  if (!dateString) return null;
  // Split the date string and create a date in local timezone
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * RecurrenceSelector - 3-card layout for selecting event type
 * Options: "just this once", "repeating event", "multiple specific dates"
 * 
 * Used by both EventPostingWizard and EditEvent for consistent UX
 * 
 * @param {Object} value - Current recurrence settings
 * @param {string} value.type - 'single' | 'repeating' | 'multiple'
 * @param {number} value.repeatEveryNumber - Repeat every N periods
 * @param {string} value.repeatEveryPeriod - 'day' | 'week' | 'month'
 * @param {string[]} value.repeatOn - Days of week for weekly repeat
 * @param {string} value.monthlyType - 'dayOfMonth' | 'dayOfWeek'
 * @param {string} value.repeatEndsOn - End date for repeating events
 * @param {Object[]} value.extraDates - Additional dates for 'multiple' type
 * @param {Function} onChange - Callback when value changes
 * @param {Object} baseDate - The primary date/time info
 * @param {string} baseDate.startDate - Primary start date (YYYY-MM-DD)
 * @param {string} baseDate.startTime - Primary start time (HH:MM)
 * @param {string} baseDate.endTime - Primary end time (HH:MM)
 * @param {boolean} compact - Use more compact styling (for wizard)
 */
const RecurrenceSelector = React.memo(({ 
  value = {}, 
  onChange, 
  baseDate,
  compact = false 
}) => {
  // Helper to get default end date (1 month from start date or today)
  const getDefaultEndDate = () => {
    const start = baseDate?.startDate ? new Date(baseDate.startDate) : new Date();
    // Parse as local date to avoid timezone issues
    if (baseDate?.startDate) {
      const [year, month, day] = baseDate.startDate.split('-').map(Number);
      start.setFullYear(year, month - 1, day);
    }
    // Add 1 month
    start.setMonth(start.getMonth() + 1);
    const y = start.getFullYear();
    const m = String(start.getMonth() + 1).padStart(2, '0');
    const d = String(start.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleTypeChange = (type) => {
    onChange({
      ...value,
      type,
      // Reset relevant fields when switching
      ...(type === 'single' ? { repeatEndsOn: null, extraDates: [] } : {}),
      ...(type === 'repeating' ? { extraDates: [], repeatEndsOn: getDefaultEndDate() } : {}),
      ...(type === 'multiple' ? { repeatEndsOn: null, repeatEveryNumber: 1, repeatEveryPeriod: 'week' } : {}),
    });
  };

  const today = new Date().toISOString().split('T')[0];
  
  // Get minimum end date (at least the base date)
  const minEndDate = baseDate?.startDate || today;

  const addExtraDate = () => {
    const newDate = {
      startDate: '',
      startTime: baseDate?.startTime || '',
      endTime: baseDate?.endTime || '',
    };
    onChange({
      ...value,
      extraDates: [...(value.extraDates || []), newDate],
    });
  };

  const updateExtraDate = (index, field, val) => {
    const newDates = [...(value.extraDates || [])];
    newDates[index] = { ...newDates[index], [field]: val };
    onChange({
      ...value,
      extraDates: newDates,
    });
  };

  const removeExtraDate = (index) => {
    onChange({
      ...value,
      extraDates: (value.extraDates || []).filter((_, i) => i !== index),
    });
  };

  const openNativePicker = (e) => {
    try {
      e.currentTarget.showPicker?.();
    } catch {
      // picker already open or unsupported
    }
  };

  return (
    <div className="recurrence-selector">
      {/* Type selection - big, clear cards */}
      <div className="flex flex-col gap-kit-12 mb-kit-16">
        {/* Single event option */}
        <OptionCard
          selected={value.type === 'single'}
          onClick={() => handleTypeChange('single')}
          icon={<IoCalendarOutline />}
          title="just this once"
          subtitle="single event on one date"
        />

        {/* Repeating event option */}
        <OptionCard
          selected={value.type === 'repeating'}
          onClick={() => handleTypeChange('repeating')}
          icon={<IoRepeat />}
          title="repeating event"
          subtitle="same event every week, month, etc."
        />

        {/* Repeating event settings - directly below repeating card when selected */}
        {value.type === 'repeating' && (
          <RepeatingEventSettings
            value={value}
            onChange={onChange}
            baseDate={baseDate}
            minEndDate={minEndDate}
          />
        )}

        {/* Multiple dates option */}
        <OptionCard
          selected={value.type === 'multiple'}
          onClick={() => handleTypeChange('multiple')}
          icon={<FaPlus size={12} />}
          title="multiple specific dates"
          subtitle="different dates that aren't a pattern"
        />
      </div>

      {/* Multiple dates settings */}
      {value.type === 'multiple' && (
        <div className="inyc-panel is-sm">
          <div className="text-kit-xs font-['Primary-Medium'] text-[var(--inyc-primary-text-color)] mb-kit-10">
            add more dates for this event
          </div>
          
          {/* Show base date as first date (read-only) */}
          {baseDate?.startDate && (
            <div className="inyc-panel is-sm recurrence-primary-date mb-kit-8 opacity-60 text-kit-2xs">
              <div className="flex items-center gap-kit-12 pl-kit-20">
                <div className="flex items-center gap-kit-6 shrink-0">
                  <IoCalendarOutline size={14} className="shrink-0" />
                  <span>
                    {parseDateAsLocal(baseDate.startDate)?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) || ''}
                  </span>
                </div>
                <div className="ml-auto flex items-center gap-kit-8 min-w-0 pr-[2rem]">
                  <span>{formatTime12h(baseDate.startTime)}</span>
                  <span className="opacity-40 -translate-x-[3px] text-center">to</span>
                  <span>{formatTime12h(baseDate.endTime)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Extra dates - stacked layout for mobile */}
          {(value.extraDates || []).map((date, index) => (
            <div key={index} className="inyc-panel is-sm mb-kit-8">
              {/* Top row: Date input + X button */}
              <div className="flex items-center gap-kit-8 mb-kit-6">
                <input
                  type="date"
                  value={date.startDate}
                  onChange={(e) => updateExtraDate(index, 'startDate', e.target.value)}
                  min={today}
                  onClick={openNativePicker}
                  className="recurrence-extra-datetime flex-1 cursor-pointer bg-transparent text-[var(--inyc-primary-text-color)] border-none outline-none"
                />
                <IconButton label="remove date" variant="danger" size="xs" icon={<IoClose />} onClick={() => removeExtraDate(index)} />
              </div>
              
              {/* Bottom row: Time inputs aligned with date input (not X button) */}
              <div className="flex items-center gap-kit-8 pr-[2rem]">
                <input
                  type="time"
                  step={900}
                  value={date.startTime}
                  onChange={(e) => updateExtraDate(index, 'startTime', e.target.value)}
                  onClick={openNativePicker}
                  className="recurrence-extra-datetime flex-1 cursor-pointer bg-transparent text-[var(--inyc-primary-text-color)] border-none outline-none"
                />
                <span className="opacity-40 text-kit-2xs shrink-0">to</span>
                <input
                  type="time"
                  step={900}
                  value={date.endTime}
                  onChange={(e) => updateExtraDate(index, 'endTime', e.target.value)}
                  onClick={openNativePicker}
                  className="recurrence-extra-datetime flex-1 cursor-pointer bg-transparent text-[var(--inyc-primary-text-color)] border-none outline-none"
                />
              </div>
            </div>
          ))}

          {/* Add date button */}
          <KitButton variant="secondary" size="sm" icon={<FaPlus />} onClick={addExtraDate}>
          add another date
        </KitButton>
        </div>
      )}
    </div>
  );
});

export default RecurrenceSelector;

